import { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { routes } from "@/config/pages-registry"

/**
 * RouteAnnouncer — công bố danh sách route của web ra ngoài để trình editor
 * (Upbo / preview cha) liệt kê & cho người dùng điều hướng giữa các trang,
 * giống Lovable / Google AI Studio.
 *
 * - Đặt `window.__UPBO_ROUTES__` để parent đọc trực tiếp khi cần.
 * - postMessage `{ type: 'upbo:routes', routes, current }` lên parent mỗi khi
 *   danh sách hoặc route hiện tại thay đổi. (Chiều điều hướng ngược lại
 *   `upbo:navigate` đã được __upbo_editor.js xử lý sẵn.)
 *
 * [BLACK BOX] — gắn sẵn trong App.tsx, không cần sửa.
 */
export function RouteAnnouncer() {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()

  // Announce routes + current location to the parent editor.
  useEffect(() => {
    const list = routes.map((r) => ({
      path: r.path,
      label: r.label ?? r.path,
      nav: r.nav ?? false,
    }))

    ;(window as any).__UPBO_ROUTES__ = list

    try {
      window.parent?.postMessage(
        { type: "upbo:routes", routes: list, current: pathname + search + hash },
        "*"
      )
    } catch {
      /* cross-origin parent — ignore */
    }
  }, [pathname, search, hash])

  // Inbound navigation from the editor's address bar / route list. The deployed
  // page strips __upbo_editor.js, so the handler MUST live here (bundled) to work
  // on the real Cloudflare deploy — not just inside the editor.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data
      if (d && d.type === "upbo:navigate" && typeof d.path === "string") {
        navigate(d.path)
      }
    }
    window.addEventListener("message", onMsg)
    // Respond to a parent that asks for the route list on demand.
    try {
      window.parent?.postMessage(
        { type: "upbo:routes", routes: (window as any).__UPBO_ROUTES__ ?? [], current: pathname + search + hash },
        "*"
      )
    } catch {}
    return () => window.removeEventListener("message", onMsg)
  }, [navigate, pathname, search, hash])

  return null
}
